import type { ReactNode } from "react";
import { Box, Card, SimpleGrid, Skeleton, SkeletonCircle, SkeletonText, Stack, type SimpleGridProps } from "@chakra-ui/react";

/**
 * What a page shows while its data is on the way: the shape of what is coming,
 * not a line saying "Loading…".
 *
 * A sentence tells somebody to wait without telling them what for, and the
 * page jumps when the real thing arrives at a different height. A shape holds
 * roughly the right space, so the arrival is a fill rather than a jolt. One
 * shape per kind of layout -- a table is `TableSkeleton` -- so every page
 * loads the same way.
 *
 * Each takes a `label` for screen readers, who are told it is loading and
 * what, where a sighted reader sees the shape.
 *
 * Show one while the query is `undefined`, which is what a Convex query is
 * until it resolves -- never on `(rows ?? []).length === 0`, which treats "not
 * loaded yet" and "there are none" as the same thing, so a page announces that
 * it is empty and then contradicts itself half a second later.
 */
function Busy({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box role="status" aria-busy="true" aria-label={label}>
      {children}
    </Box>
  );
}

/** Rows one under another: notifications, reviews, a dashboard list. */
export function ListSkeleton({ label, rows = 4, avatar = false }: { label: string; rows?: number; avatar?: boolean }) {
  return (
    <Busy label={label}>
      <Stack gap={3}>
        {Array.from({ length: rows }, (_, index) => (
          <Box key={index} borderWidth="1px" borderColor="border" bg="bg" px={4} py={4}>
            <Stack direction="row" gap={4} align="center">
              {avatar ? <SkeletonCircle size="9" flexShrink={0} /> : null}
              <Stack gap={2} flex="1">
                <Skeleton height="4" width="45%" />
                <Skeleton height="3" width="75%" opacity={0.6} />
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Busy>
  );
}

/** Cards in a grid: group members, activity columns, admin tiles. */
export function CardGridSkeleton({
  label,
  cards = 6,
  columns = { base: 1, md: 2, xl: 3 },
}: {
  label: string;
  cards?: number;
  columns?: SimpleGridProps["columns"];
}) {
  return (
    <Busy label={label}>
      <SimpleGrid columns={columns} gap={4}>
        {Array.from({ length: cards }, (_, index) => (
          <Card.Root key={index} borderWidth="1px" borderColor="border">
            <Card.Body p={4} gap={3}>
              <Skeleton height="4" width="60%" />
              <SkeletonText noOfLines={2} gap={2} opacity={0.6} />
            </Card.Body>
          </Card.Root>
        ))}
      </SimpleGrid>
    </Busy>
  );
}

/** One record: a card of headed paragraphs, as a detail page opens with. */
export function DetailSkeleton({ label, sections = 1 }: { label: string; sections?: number }) {
  return (
    <Busy label={label}>
      <Stack gap={6}>
        {Array.from({ length: sections }, (_, index) => (
          <Card.Root key={index} borderWidth="1px" borderColor="border">
            <Card.Body p={6} gap={4}>
              <Skeleton height="5" width="40%" />
              <SkeletonText noOfLines={3} gap={3} opacity={0.6} />
            </Card.Body>
          </Card.Root>
        ))}
      </Stack>
    </Busy>
  );
}

/** A form control whose choices are still coming: the control's own size. */
export function FieldSkeleton({ label, height = "9" }: { label: string; height?: string }) {
  return (
    <Busy label={label}>
      <Skeleton height={height} width="full" />
    </Busy>
  );
}
